import React, { FC, useState, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Modal from '@salesforce/design-system-react/components/modal';
import Button from '@salesforce/design-system-react/components/button';
import RadioGroup from '@salesforce/design-system-react/components/radio-group';
import Radio from '@salesforce/design-system-react/components/radio-group/radio';
import Textarea from '@salesforce/design-system-react/components/textarea';
import Spinner from '@salesforce/design-system-react/components/spinner';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import { GET_MANUAL_DISPOSITION_REASONS } from '../../../graphql/getManualDispositionReasons';
import UPDATE_MANUAL_DISPOSITION from '../../../graphql/mutations/updateManualDisposition';
import './index.scss';

const MANUAL_OVERRIDE_REASON = 'Manual Override/Others (Add reason for disposition)';
const MAX_COMMENT_LENGTH = 500;

interface ManualDispositionReason {
  id: number;
  reasonDescription: string;
}

interface ManualDispositionModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventExternalId: string;
  onSuccess?: () => void;
}

const ManualDispositionModal: FC<ManualDispositionModalProps> = ({
  isOpen,
  onClose,
  eventExternalId,
  onSuccess,
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ reason?: string; comment?: string }>({});

  const { data: reasonsData, loading: reasonsLoading } = useQuery(GET_MANUAL_DISPOSITION_REASONS, {
    fetchPolicy: 'no-cache',
  });

  const [updateManualDisposition, { loading: mutationLoading }] = useMutation(
    UPDATE_MANUAL_DISPOSITION,
    {
      onCompleted: data => {
        if (data.updateManualDisposition.message === 'Manual disposition updated successfully') {
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        }
      },
      onError: error => {
        console.error('Manual disposition error:', error);
      },
    }
  );

  const loading = reasonsLoading || mutationLoading;
  const reasons: ManualDispositionReason[] = reasonsData?.getManualDispositionReasons || [];

  const validateForm = useCallback((): boolean => {
    const newErrors: { reason?: string; comment?: string } = {};

    if (!selectedReasonId) {
      newErrors.reason = 'Please select a disposition reason';
    }

    const isOthersSelected = reasons?.find(
      r => String(r.id) === selectedReasonId && r.reasonDescription === MANUAL_OVERRIDE_REASON
    );
    if (isOthersSelected && !comment.trim()) {
      newErrors.comment = 'Comment is required when "Others" is selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [selectedReasonId, comment, reasons]);

  const handleSubmit = useCallback((): void => {
    if (validateForm()) {
      updateManualDisposition({
        variables: {
          eventExternalId,
          manualDispositionReasonId: parseInt(selectedReasonId, 10),
          manualDispositionComment: comment.trim() || undefined,
        },
      });
    }
  }, [validateForm, updateManualDisposition, eventExternalId, selectedReasonId, comment]);

  const handleReasonChange = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setSelectedReasonId(String(value));
    setErrors(prev => ({ ...prev, reason: undefined }));
  }, []);

  const handleCommentChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = event.currentTarget.value || '';
    setComment(value);
    if (value.trim()) {
      setErrors(prev => ({ ...prev, comment: undefined }));
    }
  }, []);

  return (
    <IconSettings iconPath="/icons">
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        dismissOnClickOutside={false}
        ariaHideApp={false}
        heading="Manual Event Disposition"
        size="medium"
        footer={[
          <div className="slds-align_absolute-center" key="footer_buttons">
            {loading && (
              <Spinner size="small" variant="base" assistiveText={{ label: 'Processing...' }} />
            )}
            <Button label="Cancel" onClick={onClose} disabled={loading} />
            <Button
              label={loading ? 'Processing...' : 'Confirm Disposition'}
              variant="brand"
              onClick={handleSubmit}
              disabled={loading}
              style={{ marginLeft: '0.5rem' }}
            />
          </div>,
        ]}
      >
        <section className="manual-disposition-modal-content">
          <div className="slds-p-around_medium">
            <div className="slds-m-bottom_medium">
              <fieldset className="slds-form-element">
                <legend className="slds-form-element__legend slds-form-element__label">
                  <abbr className="slds-required" title="required">
                    *{' '}
                  </abbr>
                  Disposition Reason
                </legend>
                <div className="slds-form-element__control">
                  <RadioGroup onChange={handleReasonChange} value={selectedReasonId}>
                    {reasons.map(reason => (
                      <Radio
                        key={reason.id}
                        id={String(reason.id)}
                        labels={{ label: reason.reasonDescription }}
                        value={String(reason.id)}
                        checked={selectedReasonId === String(reason.id)}
                        variant="base"
                      />
                    ))}
                  </RadioGroup>
                </div>
                {errors.reason && (
                  <div className="slds-form-element__help slds-text-color_error">
                    {errors.reason}
                  </div>
                )}
              </fieldset>
            </div>

            {(() => {
              const isOthersSelected = reasons?.find(
                r =>
                  String(r.id) === selectedReasonId &&
                  r.reasonDescription === MANUAL_OVERRIDE_REASON
              );
              return (
                isOthersSelected && (
                  <div className="slds-m-bottom_medium">
                    <Textarea
                      placeholder="Please provide additional details"
                      value={comment}
                      onChange={handleCommentChange}
                      required
                      error={errors.comment}
                      errorText={errors.comment}
                      maxLength={MAX_COMMENT_LENGTH}
                    />
                  </div>
                )
              );
            })()}

            <div className="slds-notify slds-notify_alert slds-alert_warning" role="alert">
              <span className="slds-assistive-text">warning</span>
              <div className="slds-notify__content">
                <p className="slds-text-body_regular">
                  The event will be completed automatically within 1 hour.
                </p>
              </div>
            </div>
          </div>
        </section>
      </Modal>
    </IconSettings>
  );
};

export default ManualDispositionModal;
